import { Head } from "@inertiajs/react";
import { Box, Section, Tabs } from "@radix-ui/themes";
import { useMemo, useState } from "react";
import { TrackManagerApplication } from "@/types/shared";
import ManagerPageHeader from "@/shared/manager/ManagerPageHeader";
import ApplicationsCards from "@/shared/track_manager/ApplicationsCards";
import ApplicationQueue from "@/shared/track_manager/ApplicationQueue";
import useIsMobile from "@/shared/hooks/useIsMobile";

interface Props {
  applications: TrackManagerApplication[];
}

type Scope = "upcoming" | "history";

/** Local date as `YYYY-MM-DD`, to compare against the serialized `event_date_raw`. */
function todayIso(): string {
  const now = new Date();
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

export default function TrackManagerApplicationsIndex({ applications }: Props) {
  const isMobile = useIsMobile();

  // The day-to-day view is "who's coming to the event I'm about to run", so that's
  // the default. This is a time split, not a status one — an approved application
  // for tomorrow still belongs here.
  const [scope, setScope] = useState<Scope>("upcoming");

  const { upcoming, history } = useMemo(() => {
    const today = todayIso();
    return {
      upcoming: applications.filter((a) => a.event_date_raw >= today),
      history: applications.filter((a) => a.event_date_raw < today),
    };
  }, [applications]);

  const visible = scope === "upcoming" ? upcoming : history;

  return (
    <div className="flex">
      <Head title="Applications" />
      <div className="grow min-w-0">
        <ManagerPageHeader title="Applications" />
        <Section size="1">
          <Tabs.Root value={scope} onValueChange={(value) => setScope(value as Scope)}>
            <Tabs.List>
              <Tabs.Trigger value="upcoming">
                Upcoming Events ({upcoming.length})
              </Tabs.Trigger>
              <Tabs.Trigger value="history">History ({history.length})</Tabs.Trigger>
            </Tabs.List>
          </Tabs.Root>

          <Box mt="4" px={{ initial: "3", sm: "0" }}>
            {isMobile ? (
              // Fresh state per tab: a card left open in one scope shouldn't be
              // open in the other.
              <ApplicationsCards key={scope} applications={visible} />
            ) : (
              <ApplicationQueue
                key={scope}
                applications={visible}
                emptyMessage={
                  scope === "upcoming"
                    ? "No applications for an upcoming event yet."
                    : "No applications from past events."
                }
              />
            )}
          </Box>
        </Section>
      </div>
    </div>
  );
}
